import { Technology } from '@/types';
import { capitalize } from '@/utils/lodash.util';
import { formatPrettyName, TECHNOLOGY_FIELDS } from '@/utils/mappings.util';

export function parseTechnologyVersion(
  technology: Technology | null | undefined,
  detailed = false
): string | null {
  if (!technology) {
    return null;
  }

  const { version, update, edition, language, sw_edition, target_sw, other } =
    technology;

  const versionParts = [
    version,
    update,
    edition,
    language,
    sw_edition,
    target_sw,
    other,
  ]
    .filter(Boolean) // Removes null, undefined, and empty strings
    .filter(part => String(part).toLowerCase() !== 'any'); // Removes "any"

  const versionString = versionParts.join(' ');

  if (!versionString) {
    // This case hits when all parts were "any" or empty
    return detailed ? 'Unknown version' : 'unknown';
  }

  return capitalize(versionString).split('\\').join('');
}

export function getTechnologyDisplayProduct(
  technology: Technology | null | undefined
): string {
  if (!technology) {
    return '-';
  }

  const { name, vendor, product } = technology;

  // Remove backslash escaping from CPE format (e.g., "react\ project" -> "react project")
  const unescapedName = name?.replace(/\\(.)/g, '$1');
  const unescapedVendor = vendor?.replace(/\\(.)/g, '$1');

  // If there's no beautified name, fall back to the raw product
  if (!unescapedName) {
    return formatPrettyName(product?.replace(/\\(.)/g, '$1')) || '-';
  }

  // If name and vendor are the same (e.g., "jQuery"), return the name
  if (unescapedName.toLowerCase() === unescapedVendor?.toLowerCase()) {
    return unescapedName;
  }

  // If the name starts with the vendor, remove the vendor prefix to get the product
  if (
    unescapedVendor &&
    unescapedName.toLowerCase().startsWith(unescapedVendor.toLowerCase() + ' ')
  ) {
    return unescapedName.substring(unescapedVendor.length).trim();
  }

  // As a final fallback, return the full beautified name
  return unescapedName;
}

export function getTechnologyDisplayVendor(
  technology: Technology | null | undefined
): string {
  if (!technology) {
    return '-';
  }

  const { name, vendor } = technology;

  if (!vendor) {
    return '-';
  }

  // Remove backslash escaping from CPE format (e.g., "react\ project" -> "react project")
  const unescapedVendor = vendor.replace(/\\(.)/g, '$1');

  // If a beautified 'name' isn't available, just capitalize the raw vendor.
  if (!name) {
    return formatPrettyName(unescapedVendor);
  }

  // If name and vendor are the same (e.g., "jQuery"), 'name' is the beautified vendor.
  if (name.toLowerCase() === unescapedVendor.toLowerCase()) {
    return name;
  }

  // If the beautified 'name' starts with the raw vendor, extract the prefix from 'name'.
  // This handles cases like name="OpenBSD OpenSSH" and vendor="openbsd" -> "OpenBSD"
  if (name.toLowerCase().startsWith(unescapedVendor.toLowerCase() + ' ')) {
    return name.substring(0, unescapedVendor.length);
  }

  // As a fallback, just capitalize the raw vendor string.
  return formatPrettyName(unescapedVendor);
}

// Re-export for backward compatibility
export { TECHNOLOGY_FIELDS };
